import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL;
const headers = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

// KnowBe4 risk score is 0–100 where HIGHER = MORE risk (0 is best).
const riskBadge = (s) => {
  if (typeof s !== 'number') return { bg: '#F3F4F6', fg: '#6B7280', label: '—' };
  const v = Math.round(s);
  if (s >= 70) return { bg: '#FEF2F2', fg: '#B91C1C', label: v };   // high
  if (s >= 30) return { bg: '#FFFBEB', fg: '#B45309', label: v };   // medium
  return { bg: '#ECFDF5', fg: '#047857', label: v };                // low
};

// Compact training summary for a table cell: red overdue count, else "passed/total done".
const trainingCell = (t) => {
  const overdue = t?.overdue || 0;
  if (overdue > 0) return { text: `${overdue} overdue`, color: '#B91C1C', weight: 700 };
  return { text: `${t?.passed ?? 0}/${t?.total ?? 0} done`, color: '#6B7280', weight: 500 };
};

// Status pill colors for a training module in the drill-down.
const bucketPill = (bucket) => {
  if (bucket === 'overdue') return { bg: '#FEF2F2', fg: '#B91C1C' };
  if (bucket === 'passed') return { bg: '#ECFDF5', fg: '#047857' };
  if (bucket === 'in_progress') return { bg: '#FFFBEB', fg: '#B45309' };
  return { bg: '#F3F4F6', fg: '#6B7280' };
};

const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

export default function KnowBe4Report() {
  const [data, setData]       = useState(null);  // { configured, users, summary }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [search, setSearch]   = useState('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [sortKey, setSortKey] = useState('risk_score'); // 'risk_score' | 'phish' | 'overdue' | 'name'
  const [sortDir, setSortDir] = useState('desc');       // 'asc' | 'desc'

  // Per-user drill-down modal
  const [selected, setSelected]           = useState(null); // { id, name } while open
  const [detail, setDetail]               = useState(null); // { user, training, last_phishing }
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError]     = useState('');

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/knowbe4/users`, { headers: headers() });
      setData(res.data);
      setError('');
    } catch (e) {
      if (e.response?.status === 403) setError('You are not authorized to view this report.');
      else if (e.response?.status === 502) setError('KnowBe4 API error — check the token or try again shortly.');
      else setError(e.response?.data?.error || 'Failed to load KnowBe4 report.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const users = data?.users || [];
  const summary = data?.summary || {};

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = users.filter(u =>
      (!q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)) &&
      (!overdueOnly || (u.training?.overdue || 0) > 0)
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    const numOf = (u) => {
      if (sortKey === 'overdue') return u.training?.overdue || 0;
      const key = sortKey === 'phish' ? 'phish_prone_percentage' : 'risk_score';
      return typeof u[key] === 'number' ? u[key] : -1;
    };
    out = [...out].sort((a, b) => {
      if (sortKey === 'name') return dir * (a.name || '').localeCompare(b.name || '');
      return dir * (numOf(a) - numOf(b));
    });
    return out;
  }, [users, search, overdueOnly, sortKey, sortDir]);

  const openUser = async (u) => {
    setSelected({ id: u.id, name: u.name });
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/knowbe4/users/${u.id}`, { headers: headers() });
      setDetail(res.data);
    } catch (e) {
      setDetailError(e.response?.data?.error || 'Failed to load user detail.');
    } finally {
      setDetailLoading(false);
    }
  };
  const closeUser = () => { setSelected(null); setDetail(null); setDetailError(''); };

  const setSort = (key) => {
    if (sortKey === key) { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); return; }
    setSortKey(key);
    setSortDir(key === 'name' ? 'asc' : 'desc');
  };

  const exportCSV = () => {
    const headerRow = ['Name', 'Email', 'Risk Score', 'Phish-Prone %', 'Training Overdue', 'Training Passed', 'Training Total'];
    const body = rows.map(u => [
      u.name || '',
      u.email || '',
      typeof u.risk_score === 'number' ? Math.round(u.risk_score) : '',
      typeof u.phish_prone_percentage === 'number' ? u.phish_prone_percentage : '',
      u.training?.overdue ?? 0,
      u.training?.passed ?? 0,
      u.training?.total ?? 0,
    ]);
    const csv = [headerRow, ...body].map(row => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `caam-knowbe4-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Styles (match TimeOffReport conventions)
  const card = { background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '20px 24px', marginBottom: '16px' };
  const statLabel = { fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' };
  const statNum = { fontSize: '26px', fontWeight: 700, color: 'var(--caam-text)', lineHeight: 1 };
  const th = (key, label, align = 'left') => (
    <th
      onClick={() => setSort(key)}
      style={{ textAlign: align, padding: '10px 14px', fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      {label}{sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Header */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, color: 'var(--caam-text)', margin: '0 0 4px' }}>KnowBe4 Security Report</h2>
            <p style={{ fontSize: '12px', color: 'var(--caam-text-muted)', margin: 0 }}>
              Org-wide security-awareness scores. Risk score is 0–100 (lower is better); higher = more risk. Admin-only.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={fetchReport} disabled={loading}
              style={{ padding: '8px 14px', background: '#fff', color: 'var(--caam-text)', border: '1px solid var(--caam-border)', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: loading ? 'default' : 'pointer' }}>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
            <button onClick={exportCSV} disabled={rows.length === 0}
              style={{ padding: '8px 14px', background: rows.length === 0 ? '#E5E7EB' : 'var(--caam-accent)', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: rows.length === 0 ? 'default' : 'pointer' }}>
              Export CSV ({rows.length})
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ ...card, background: '#FEF2F2', color: '#B91C1C', fontSize: '13px' }}>{error}</div>
      )}

      {loading && !data && (
        <div style={{ ...card, color: 'var(--caam-text-muted)', fontSize: '13px' }}>Loading KnowBe4 data…</div>
      )}

      {data && data.configured === false && !error && (
        <div style={{ ...card, color: 'var(--caam-text-muted)', fontSize: '13px' }}>
          KnowBe4 API token is not configured on the server.
        </div>
      )}

      {data && data.configured && (
        <>
          {/* Summary stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px', marginBottom: '16px' }}>
            <div style={card}>
              <div style={statLabel}>Users</div>
              <div style={statNum}>{summary.count ?? '—'}</div>
            </div>
            <div style={card}>
              <div style={statLabel}>Avg Risk Score</div>
              <div style={{ ...statNum, color: riskBadge(summary.avg_risk).fg }}>{summary.avg_risk ?? '—'}</div>
            </div>
            <div style={card}>
              <div style={statLabel}>High Risk (≥70)</div>
              <div style={{ ...statNum, color: summary.high_risk > 0 ? '#B91C1C' : 'var(--caam-text)' }}>{summary.high_risk ?? '—'}</div>
            </div>
            <div style={card}>
              <div style={statLabel}>Avg Phish-Prone</div>
              <div style={statNum}>{typeof summary.avg_phish_prone === 'number' ? `${summary.avg_phish_prone}%` : '—'}</div>
            </div>
            <div style={card}>
              <div style={statLabel}>Overdue Trainings</div>
              <div style={{ ...statNum, color: summary.total_overdue > 0 ? '#B91C1C' : 'var(--caam-text)' }}>{summary.total_overdue ?? '—'}</div>
              <div style={{ fontSize: '11px', color: 'var(--caam-text-muted)', marginTop: '4px' }}>across {summary.overdue_users ?? 0} users</div>
            </div>
          </div>

          {/* Search + table */}
          <div style={{ ...card, padding: '0', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--caam-border)', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                style={{ flex: '1 1 240px', maxWidth: '360px', padding: '8px 12px', border: '1px solid var(--caam-border)', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
              />
              <button
                onClick={() => setOverdueOnly(v => !v)}
                aria-pressed={overdueOnly}
                style={{
                  padding: '8px 14px', borderRadius: '999px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                  border: overdueOnly ? '1px solid #FCA5A5' : '1px solid var(--caam-border)',
                  background: overdueOnly ? '#FEF2F2' : '#fff',
                  color: overdueOnly ? '#B91C1C' : 'var(--caam-text-muted)'
                }}
              >
                {overdueOnly ? '● ' : ''}Overdue training only
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--caam-border)', background: '#FAFAFA' }}>
                    {th('name', 'Name')}
                    {th('name', 'Email')}
                    {th('risk_score', 'Risk Score', 'center')}
                    {th('phish', 'Phish-Prone', 'center')}
                    {th('overdue', 'Training', 'center')}
                    <th style={{ width: '24px' }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((u, i) => {
                    const b = riskBadge(u.risk_score);
                    const tc = trainingCell(u.training);
                    return (
                      <tr key={u.email || i} onClick={() => openUser(u)} title="View details"
                        style={{ borderBottom: '1px solid #F1F1F3', cursor: 'pointer' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '11px 14px', fontSize: '13px', fontWeight: 500, color: 'var(--caam-text)', whiteSpace: 'nowrap' }}>{u.name || '—'}</td>
                        <td style={{ padding: '11px 14px', fontSize: '13px', color: 'var(--caam-text-muted)' }}>{u.email || '—'}</td>
                        <td style={{ padding: '11px 14px', textAlign: 'center' }}>
                          <span style={{ display: 'inline-block', minWidth: '34px', padding: '3px 8px', borderRadius: '999px', background: b.bg, color: b.fg, fontSize: '12px', fontWeight: 700 }}>{b.label}</span>
                        </td>
                        <td style={{ padding: '11px 14px', textAlign: 'center', fontSize: '13px', color: 'var(--caam-text)' }}>
                          {typeof u.phish_prone_percentage === 'number' ? `${u.phish_prone_percentage}%` : '—'}
                        </td>
                        <td style={{ padding: '11px 14px', textAlign: 'center', fontSize: '13px', fontWeight: tc.weight, color: tc.color }}>
                          {tc.text}
                        </td>
                        <td style={{ padding: '11px 8px', textAlign: 'center', color: '#C4C4C8' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr><td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: 'var(--caam-text-muted)', fontSize: '13px' }}>
                      {users.length === 0 ? 'No KnowBe4 users found.' : 'No users match your search.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Per-user drill-down modal */}
      {selected && (
        <div
          onClick={closeUser}
          style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '48px 16px', zIndex: 1000 }}
        >
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: '14px', width: '100%', maxWidth: '580px', maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', padding: '20px 24px', borderBottom: '1px solid var(--caam-border)', position: 'sticky', top: 0, background: '#fff', borderTopLeftRadius: '14px', borderTopRightRadius: '14px' }}>
              <div>
                <h3 style={{ margin: '0 0 2px', fontSize: '17px', fontWeight: 600, color: 'var(--caam-text)' }}>{selected.name}</h3>
                <div style={{ fontSize: '12px', color: 'var(--caam-text-muted)' }}>{detail?.user?.email || ''}</div>
              </div>
              <button onClick={closeUser} aria-label="Close"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--caam-text-muted)', fontSize: '22px', lineHeight: 1, padding: '2px 6px' }}>×</button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              {detailLoading && <div style={{ color: 'var(--caam-text-muted)', fontSize: '13px' }}>Loading…</div>}
              {detailError && <div style={{ color: '#B91C1C', fontSize: '13px' }}>{detailError}</div>}

              {detail && detail.user && (
                <>
                  {/* Score row */}
                  <div style={{ display: 'flex', gap: '24px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Risk Score</div>
                      {(() => { const b = riskBadge(detail.user.risk_score); return (
                        <span style={{ display: 'inline-block', minWidth: '38px', padding: '4px 12px', borderRadius: '999px', background: b.bg, color: b.fg, fontSize: '16px', fontWeight: 700 }}>{b.label}</span>
                      ); })()}
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Phish-Prone</div>
                      <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--caam-text)' }}>{typeof detail.user.phish_prone_percentage === 'number' ? `${detail.user.phish_prone_percentage}%` : '—'}</div>
                    </div>
                    {detail.last_phishing && (
                      <div>
                        <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>Last Phish</div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: detail.last_phishing.clicked ? '#B91C1C' : 'var(--caam-text)' }}>{detail.last_phishing.status}</div>
                        <div style={{ fontSize: '11px', color: 'var(--caam-text-muted)' }}>{detail.last_phishing.campaign}</div>
                      </div>
                    )}
                  </div>

                  {/* Training summary chips */}
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Overdue', val: detail.training?.overdue || 0, bucket: 'overdue' },
                      { label: 'In progress', val: detail.training?.in_progress || 0, bucket: 'in_progress' },
                      { label: 'Passed', val: detail.training?.passed || 0, bucket: 'passed' },
                    ].map(c => { const p = bucketPill(c.bucket); return (
                      <span key={c.label} style={{ padding: '4px 10px', borderRadius: '8px', background: p.bg, color: p.fg, fontSize: '12px', fontWeight: 600 }}>{c.val} {c.label}</span>
                    ); })}
                  </div>

                  {/* Module list */}
                  <div style={{ fontSize: '11px', fontWeight: 600, color: 'var(--caam-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px' }}>
                    Training Modules ({detail.training?.total ?? 0})
                  </div>
                  <div style={{ border: '1px solid var(--caam-border)', borderRadius: '8px', overflow: 'hidden' }}>
                    {(detail.training?.items || []).map((it, i) => { const p = bucketPill(it.bucket); return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '10px 12px', borderBottom: i < detail.training.items.length - 1 ? '1px solid #F1F1F3' : 'none' }}>
                        <span style={{ fontSize: '13px', color: 'var(--caam-text)' }}>{it.module}</span>
                        <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: '999px', background: p.bg, color: p.fg, fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap' }}>{it.status || '—'}</span>
                      </div>
                    ); })}
                    {(detail.training?.items || []).length === 0 && (
                      <div style={{ padding: '14px', textAlign: 'center', color: 'var(--caam-text-muted)', fontSize: '13px' }}>No training enrollments.</div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
